import { EntityPluralLower } from '@enums/EntityName';
import EntityType from '@enums/EntityType';
import { nestedToArray } from '@utils/arrayToNested';
import isProcess from '@utils/processes/isProcess';
import addTreeItemMetrics from '@utils/treeData/addTreeItemMetrics';
import buildTreePath from '@utils/treeData/buildTreePath';
import filterEntity from '@utils/treeData/filterEntity';
import getMetrics from '@utils/treeData/getMetrics';
import initializeTree from '@utils/treeData/initializeTree';
import isSameTreePath from '@utils/treeData/isSameTreePath';
import collectEntities from './collectEntities';

const EntityOrder = Object.freeze({
  [EntityType.ACTIVITY]: 0,
  [EntityType.ROLE]: 1,
  [EntityType.PERSON]: 2,
  [EntityType.GROUP]: 3,
});

const retrieveLoopingEntity = (sources) => {
  if (sources.some((source) => isProcess(source.type))) {
    return EntityType.ACTIVITY;
  }

  let entity = null;
  let lowestEntity = EntityOrder[EntityType.GROUP];

  for (let i = 0; i < sources.length; i++) {
    // People, spans and managers are found through their roles.
    const entityType =
      sources[i].type === EntityType.PERSON ||
      sources[i].type === EntityType.SPAN ||
      sources[i].type === EntityType.MANAGER
        ? EntityType.ROLE
        : sources[i].type;
    const orderNumber = EntityOrder[entityType];

    if (orderNumber <= lowestEntity) {
      lowestEntity = orderNumber;
      entity = entityType;
    }
    if (lowestEntity === 0) {
      return entity;
    }
  }

  return entity;
};

// Managers are grouped by their span of control: their direct report count.
const spanEntity = (role) => ({
  uuid: `Span ${role.__metrics?.span?.visible?.roles ?? 0}`,
  __type: EntityType.SPAN,
  visible: true,
});

// Roles are grouped by the role that manages them.
const managerEntity = (role) => ({
  uuid: role.uuid,
  __type: EntityType.MANAGER,
  visible: true,
});

export default function nestedEntityResponse({
  body,
  designs,
  designLevel = { type: EntityType.ORGANISATION },
  entities,
  level,
  library,
  peopleSkillMap,
}) {
  const sources = body.sources;
  const settings = body.settings;
  const includeMetrics = sources.args?.includeMetrics;
  const includeEmptyValues = sources.args?.includeEmptyValues;
  const includeUnallocated = settings?.includeUnallocated;

  const flatSources = nestedToArray(sources);
  const hasSpanSource = flatSources.some(
    (source) => source.type === EntityType.SPAN
  );
  const hasManagerSource = flatSources.some(
    (source) => source.type === EntityType.MANAGER
  );

  // Define the types of entities.
  const order = flatSources.map((item) => {
    if (item?.args.ids?.length) {
      const allowedIds = new Set();
      let allowedSubIds = new Set();
      let allowedValues = new Map();

      item.args.ids.forEach((arg) => {
        allowedIds.add(arg.id);
        if (arg.ids) {
          allowedSubIds = new Set([...allowedSubIds, ...arg.ids]);
        }
        if (arg.value) {
          allowedValues.set(arg.id, new Set([...arg.value]));
        }
      });

      return {
        ...item,
        allowedIds: allowedIds.size ? allowedIds : null,
        allowedSubIds: allowedSubIds.size ? allowedSubIds : null,
        allowedValues: allowedValues.size ? allowedValues : null,
        entitySet: new Set(),
      };
    }

    return {
      ...item,
      entitySet: new Set(),
    };
  });

  // Initializes the tree with library items if they are the first item
  // in the order.
  let tree = {};

  const root = {};
  const totalEntitySet = new Set();

  // Find the lowest looping entity.
  const loopingEntity = retrieveLoopingEntity(order);

  const primaryDesign = designs.find(({ id }) => id === 'primary');

  // If we don't have any primary entities we need to exit.
  if (!primaryDesign) {
    return;
  }

  const primaryEntities = primaryDesign.entities;
  const orderedDesigns = [
    primaryDesign,
    ...designs.filter(({ id }) => id !== 'primary'),
  ];
  const loopingType = EntityPluralLower[loopingEntity];

  // Entities that only exist in a comparison design are included so removed
  // entities are reported with empty primary metrics.
  const loopingIds = new Set(
    orderedDesigns.flatMap((design) =>
      Object.keys(design.entities?.[loopingType] ?? {})
    )
  );

  // Initialize the tree for empty items if necessary library items.
  if (includeEmptyValues) {
    if (
      order[0].subType ||
      isProcess(order[0].type) ||
      EntityOrder[order[0].type] > EntityOrder[loopingEntity]
    ) {
      tree = initializeTree(order, library, primaryEntities, level);
    }
  }

  // The unfiltered root item captures unfiltered root metric items which are
  // attached to the root totals at an entity level. These are necessary for
  // calculations where the denominator should be all entity hours rather than
  // filtered entity hours (e.g. % of tag hours that should also include
  // untagged).
  const unfilteredRootItem = {};
  const unfilteredRootSet = new Set();
  const roleFilter = order.find(({ type, subType }) => {
    return (
      type === EntityType.ROLE && (!subType || subType === EntityType.ROLE_TYPE)
    );
  });

  loopingIds.forEach((id) => {
    const designEntities = orderedDesigns.map((design) =>
      collectEntities({
        design,
        entity: design.entities?.[loopingType]?.[id],
        entities: design.entities,
        id,
        includeUnallocated,
        level,
      })
    );

    designEntities.forEach(({ entities: entityCollection }, index) => {
      if (!entityCollection[loopingEntity]) {
        return;
      }

      const role = entityCollection[EntityType.ROLE];

      const manager = hasManagerSource
        ? orderedDesigns[index].entities?.[EntityPluralLower.ROLE]?.[
            role?.__manager
          ]
        : null;

      if (hasManagerSource) {
        entityCollection[EntityType.MANAGER] = manager
          ? managerEntity(manager)
          : null;
      }

      // Spans are the manager's when roles are grouped by manager.
      if (hasSpanSource) {
        const spanRole = hasManagerSource
          ? manager
          : role?.is_manager
            ? role
            : null;
        entityCollection[EntityType.SPAN] = spanRole
          ? spanEntity(spanRole)
          : null;
      }
    });

    // Designs that classify the entity the same share one tree path. A design
    // that classifies it differently follows its own path, so its metrics are
    // reported under its own items.
    for (let i = 0; i < designEntities.length; i++) {
      const design = designEntities[i];
      design.path = design.entities;

      if (!design.entities[loopingEntity]) {
        continue;
      }

      for (let j = 0; j < i; j++) {
        const other = designEntities[j];
        if (
          other.path === other.entities &&
          other.entities[loopingEntity] &&
          isSameTreePath({
            a: other.entities,
            b: design.entities,
            order,
            library,
          })
        ) {
          design.path = other.entities;
          break;
        }
      }
    }

    designEntities.forEach(({ entities: entityCollection, path }) => {
      const entity = entityCollection[loopingEntity];

      if (!entity || path !== entityCollection) {
        return;
      }

      const role = entityCollection[EntityType.ROLE];
      const group = entityCollection[EntityType.GROUP];
      const excludeRole = roleFilter ? filterEntity(role, roleFilter) : null;

      if (!excludeRole && role) {
        unfilteredRootItem.metrics = addTreeItemMetrics({
          designEntities,
          entity: role,
          entities: entityCollection,
          entitySet: unfilteredRootSet,
          treeItem: unfilteredRootItem,
        });
      }

      // If we're looping by the group entity we don't have any roles or
      // activities.
      if (loopingEntity === EntityType.GROUP && group) {
        unfilteredRootItem.metrics = addTreeItemMetrics({
          designEntities,
          entity: group,
          entities: entityCollection,
          entitySet: unfilteredRootSet,
          treeItem: unfilteredRootItem,
        });
      }

      const result = buildTreePath({
        designEntities,
        entities: entityCollection,
        includeEmptyValues,
        includeMetrics,
        library,
        orderIndex: 0,
        order,
        peopleSkillMap,
        tree,
      });

      tree = result.tree || tree;

      if (includeMetrics) {
        if (result.hasCompleteValidLeaf) {
          root.metrics = addTreeItemMetrics({
            designEntities,
            entityLevelOnly: true,
            entity,
            entities: entityCollection,
            entitySet: totalEntitySet,
            treeItem: root,
          });
        } else {
          root.metrics = root.metrics || getMetrics();
        }
      }
    });
  });

  // Extend the root metrics with the unfiltered snapshot metrics for
  // calculating % of role and group metrics.
  designs.forEach(({ id }) => {
    if (!root.metrics?.[id] || !unfilteredRootItem?.metrics?.[id]) {
      return;
    }

    root.metrics[id][EntityType.ACTIVITY] =
      unfilteredRootItem.metrics[id][EntityType.ACTIVITY];

    root.metrics[id][EntityType.ROLE] =
      unfilteredRootItem.metrics[id][EntityType.ROLE];

    root.metrics[id][EntityType.GROUP] =
      unfilteredRootItem.metrics[id][EntityType.GROUP];
  });

  return { tree, totals: root.metrics };
}
